import { useEffect, useState } from 'react';

import {
  EXTENDED_WARRANTY_2YEAR_SKU,
  EXTENDED_WARRANTY_3YEAR_SKU,
  MEMBERSHIP_1YEAR_SKU,
} from '@/app/[locale]/product/_consts';
import {
  type PDPContextType,
  usePDPContext,
} from '@/app/[locale]/product/_contexts/PDPProvider';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import Selections from '@/app/[locale]/product/_steps/enums/SizeStep';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import getCurrentStepFromRoute from '@/app/[locale]/product/_utils/getCurrentStepFromRoute';
import getExistingMembership from '@/app/[locale]/product/_utils/getExistingMembership';
import getExistingWarranty from '@/app/[locale]/product/_utils/getExistingWarranty';
import getExtendedWarrantyStep from '@/app/[locale]/product/_utils/getExtendedWarrantyStep';

import type UseProducts from '../types';
import { useButtonDisabled } from '../useButtonDisabled';
import useContentScroll from '../useContentScroll';
import useProducts from '../useProducts';
import type { UseWizardState, UseWizardStateProps } from './types';

// Implement the useWizardState
const useWizardState = ({
  currentRoute,
}: UseWizardStateProps): UseWizardState => {
  // choose between sizing kit and choose size
  const [sizingSelector, setSizingSelector] = useState<Selections>(
    Selections.SizingKit,
  );

  // add to cart loading state
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);

  // membership and warranty selection
  const [membershipSelection, setMembershipSelection] =
    useState<MembershipSelection>(MembershipSelection.OneMonth);
  const [warrantySelection, setWarrantySelection] = useState<WarrantySelection>(
    WarrantySelection.OneYear,
  );

  const [notifyModal, setNotifyModal] = useState<boolean>(false);

  // scroll to choose button on click
  const contentScroller = useContentScroll();

  // current step from route
  const currentStep = getCurrentStepFromRoute(currentRoute);
  const products: UseProducts = useProducts(currentRoute);

  const {
    chargerCount,
    getSizeStockStatus,
    itemCountForIcon,
    lineItems,
    maxChargerQuantity,
    maxRingQuantity,
    ringCount,
  }: PDPContextType = usePDPContext();

  const { finish, isAccessory, isOuraRing4Wizard, size, style } = currentRoute;
  const stockStatus = getSizeStockStatus({
    style: style.value,
    finish: finish.value,
    selectedSize: size.value,
    isOuraRing4: isOuraRing4Wizard,
  });

  const existingWarranty = getExistingWarranty(lineItems);

  // set existing warranty selection
  useEffect(() => {
    if (existingWarranty) {
      const sku = existingWarranty.skuCode;
      if (sku === EXTENDED_WARRANTY_2YEAR_SKU) {
        setWarrantySelection(WarrantySelection.TwoYear);
      } else if (sku === EXTENDED_WARRANTY_3YEAR_SKU) {
        setWarrantySelection(WarrantySelection.ThreeYear);
      } else {
        setWarrantySelection(WarrantySelection.OneYear);
      }
    }
  }, [existingWarranty]);

  const existingMembership = getExistingMembership(lineItems);

  // set existing membership selection
  useEffect(() => {
    if (existingMembership) {
      const sku = existingMembership.skuCode;
      if (sku === MEMBERSHIP_1YEAR_SKU) {
        setMembershipSelection(MembershipSelection.OneYear);
      } else {
        setMembershipSelection(MembershipSelection.OneMonth);
      }
    }
  }, [existingMembership]);

  const warrantyStep = getExtendedWarrantyStep(existingWarranty?.skuCode);

  // cart is full messaging
  const hasMaxRingCount = ringCount >= maxRingQuantity && !isAccessory;
  const hasMaxChargerCount = chargerCount >= maxChargerQuantity && isAccessory;

  const nextButtonDisabled = useButtonDisabled({
    currentStep,
    sizingSelector,
    size,
    isAccessory,
    isOuraRing4Wizard,
    stockStatus,
  });

  return {
    ...contentScroller,
    ...products,
    chargerCount,
    currentRoute,
    currentStep,
    existingMembership,
    existingWarranty,
    hasMaxChargerCount,
    hasMaxRingCount,
    isAccessory,
    isAddingToCart,
    isOuraRing4Wizard,
    itemCountForIcon,
    membershipSelection,
    nextButtonDisabled,
    notifyModal,
    ringCount,
    sizingSelector,
    stockStatus,
    warrantySelection,
    warrantyStep,
    setIsAddingToCart,
    setMembershipSelection,
    setNotifyModal,
    setSizingSelector,
    setWarrantySelection,
  };
};

export default useWizardState;
